import React from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import { Wrapper } from '../../atom/wrapper/Wrapper';
import clsx from 'clsx';
import { marked } from 'marked';
import { CaretDownIcon } from '@radix-ui/react-icons';
import { Section } from '../../atom/section/Section';
import { Heading } from '../../atom/heading/Heading';
import './style.css';

type Props = {
	title?: string;
	faqs: {
		question: string;
		answer: string;
		id: string;
	}[];
	sectionClass?: string;
	theme?: 'light' | 'dark';
};

export const FaqsSection = ({
	title = 'Frequently asked questions',
	faqs,
	sectionClass,
	theme = 'light',
}: Props) => {
	return (
		<Section
			className={clsx(sectionClass, {
				['bg-ep-steel']: theme === 'light',
				'bg-ep-navy': theme === 'dark',
			})}
		>
			<Wrapper>
				<Heading
					level="h2"
					color={theme === 'dark' ? 'text-white' : 'text-ep-navy'}
					className={' mb-12'}
				>
					{title}
				</Heading>
				<Details faqs={faqs} theme={theme} />
			</Wrapper>
		</Section>
	);
};

const Details = ({ faqs, theme }: Props) => (
	<div data-faqs>
		{faqs.map((faq, i) => (
			<details
				key={faq.id}
				className={clsx('border-b', {
					'border-b-white border-opacity-50 text-ep-white': theme === 'dark',
					'text-ep-navy border-b-ep-navy': theme === 'light',
				})}
			>
				<summary className={'text-body py-8 flex items-center cursor-pointer'}>
					<div className="flex-grow">{faq.question}</div>
					<CaretDownIcon
						className={clsx('w-6 h-6 shrink-0 ml-4', {
							'text-ep-navy': theme === 'light',
							'text-ep-white': theme === 'dark',
						})}
					/>
				</summary>
				<div
					dangerouslySetInnerHTML={{ __html: marked.parse(faq.answer) }}
					className={clsx('p__innerHtml', 'p__innerHtml--faq', 'pb-6', {
						'p__innerHtml--white': theme === 'dark',
					})}
				/>
			</details>
		))}
	</div>
);

const RadixAccordion = ({ faqs, theme }: Props) => (
	<Accordion.Root className={'w-full flex flex-col'} collapsible type="single">
		{faqs.map((faq, i) => {
			return (
				<Accordion.Item
					className={'accordion__item'}
					value={`item-${i}`}
					key={faq.id}
				>
					<Accordion.Trigger className={'accordion__trigger'}>
						<Heading
							level="h3"
							size="h6"
							color={theme === 'light' ? 'text-ep-navy' : 'text-ep-white'}
							className="text-body"
						>
							{faq.question}
						</Heading>
						<CaretDownIcon className={'accordion__caret'} />
					</Accordion.Trigger>
					<Accordion.Content className={'accordion__content'}>
						<div
							className={clsx('p__innerHtml', 'p__innerHtml--faq', 'pb-6', {
								'p__innerHtml--white': theme === 'dark',
							})}
							dangerouslySetInnerHTML={{
								__html: marked.parse(faq.answer),
							}}
						/>
					</Accordion.Content>
				</Accordion.Item>
			);
		})}
	</Accordion.Root>
);
